import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { athletesAPI, athleteAuthAPI, athleteProfilesAPI, getErrorMessage } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { User, Lock, Eye, EyeOff, LogOut, RefreshCw, UserPlus, Users } from 'lucide-react';
import { ProfilePhotoUpload } from '../../components/ProfileAvatar';
import { Navbar } from '../../components/Navbar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

export default function AthleteProfilePage() {
  const { user, logout, deleteAccount, switchToCoach } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [addingRole, setAddingRole] = useState(false);
  const [showLinkProfile, setShowLinkProfile] = useState(false);
  const [linkCode, setLinkCode] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Password
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const hasDualRole = user?.coach_id || user?.has_coach_role;

  useEffect(() => {
    if (!user) return;

    setName(user.name || '');
    setEmail(user.email || '');
    setProfilePhoto(user.profile_photo || null);

    if (hasDualRole) {
      localStorage.setItem('hasDualRole', 'true');
    } else {
      localStorage.removeItem('hasDualRole');
    }
  }, [user, hasDualRole]);

  const handleAddCoachRole = async () => {
    setAddingRole(true);
    try {
      await athleteAuthAPI.addCoachRole();
      localStorage.setItem('hasDualRole', 'true');
      toast.success('Ruolo allenatore aggiunto!');
      setTimeout(() => window.location.reload(), 1000);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setAddingRole(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    setUploadingPhoto(true);
    try {
      const res = await athleteAuthAPI.uploadPhoto(file);
      setProfilePhoto(res.data.profile_photo);
      toast.success('Foto aggiornata');
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await athletesAPI.updateMyProfile({ name, email });
      toast.success('Profilo aggiornato');
      setTimeout(() => window.location.reload(), 800);
    } catch (e2) {
      toast.error(getErrorMessage(e2));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    setPasswordLoading(true);
    try {
      await athletesAPI.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      toast.success('Password aggiornata');
      setShowPasswordSection(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e2) {
      toast.error(getErrorMessage(e2));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLinkProfile = async (e) => {
    e.preventDefault();
    setLinkLoading(true);
    try {
      await athleteProfilesAPI.linkProfile(linkCode);
      toast.success('Profilo collegato! Ora puoi passare da un figlio all\'altro.');
      setShowLinkProfile(false);
      setLinkCode('');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Codice non valido'));
    } finally {
      setLinkLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      toast.success('Account eliminato con successo');
    } catch {
      toast.error("Errore nell'eliminazione dell'account");
    }
  };

  return (
    <div className="app-shell bg-[#09090b]">
      <Navbar />

      <main className="app-scroll has-top-bar">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8">
          <div className="mb-8">
            <h1 className="text-2xl sm:text-4xl font-black text-white mb-1 sm:mb-2">
              Il Mio Profilo
            </h1>
            <p className="text-zinc-400">Gestisci le tue informazioni personali</p>
          </div>

          {/* FOTO */}
          <div className="glass-card p-6 rounded-xl border border-white/10 mb-4 flex justify-center">
            <ProfilePhotoUpload
              currentPhoto={profilePhoto}
              name={name}
              onUpload={handlePhotoUpload}
              uploading={uploadingPhoto}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            {/* INFORMAZIONI PERSONALI */}
            <div className="glass-card p-6 rounded-xl border border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <User className="w-8 h-8 text-[#ccff00]" />
                <h2 className="text-2xl font-bold text-white">Informazioni Personali</h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-zinc-300">
                    Nome Completo
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-xl h-12"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-300">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-xl h-12"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold uppercase tracking-wide rounded-xl h-12 shadow-[0_0_15px_rgba(204,255,0,0.3)]"
                >
                  {loading ? 'Salvataggio...' : 'Salva Modifiche'}
                </Button>
              </form>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {/* MODIFICA PASSWORD */}
              <div className="glass-card p-6 rounded-xl border border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <Lock className="w-6 h-6 text-[#ccff00]" />
                  <h2 className="text-xl font-bold text-white">Modifica Password</h2>
                </div>

                {!showPasswordSection ? (
                  <Button
                    onClick={() => setShowPasswordSection(true)}
                    variant="outline"
                    className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl"
                  >
                    Cambia Password
                  </Button>
                ) : (
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Password Attuale</Label>
                      <div className="relative">
                        <Input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] rounded-xl h-12 pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                        >
                          {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-zinc-300">Nuova Password</Label>
                      <div className="relative">
                        <Input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] rounded-xl h-12 pr-10"
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                        >
                          {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-zinc-300">Conferma Nuova Password</Label>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] rounded-xl h-12"
                        required
                        minLength={6}
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowPasswordSection(false);
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                        className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl"
                      >
                        Annulla
                      </Button>
                      <Button
                        type="submit"
                        disabled={passwordLoading}
                        className="flex-1 bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl"
                      >
                        {passwordLoading ? 'Salvataggio...' : 'Salva'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>

              {/* AGGIUNGI RUOLO ALLENATORE */}
              {!hasDualRole && (
                <div className="glass-card p-6 rounded-xl border border-[#ccff00]/30">
                  <div className="flex items-center gap-3 mb-4">
                    <UserPlus className="w-6 h-6 text-[#ccff00]" />
                    <h2 className="text-xl font-bold text-white">Aggiungi Ruolo Allenatore</h2>
                  </div>
                  <p className="text-zinc-400 text-sm mb-4">
                    Vuoi gestire anche i tuoi atleti? Aggiungi il ruolo allenatore per accedere a entrambe le viste.
                  </p>
                  <Button
                    onClick={handleAddCoachRole}
                    disabled={addingRole}
                    className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl shadow-[0_0_15px_rgba(204,255,0,0.3)]"
                  >
                    {addingRole ? 'Aggiunta in corso...' : 'Aggiungi Ruolo Allenatore'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* AGGIUNGI PROFILO FIGLIO */}
          <div className="mt-3 sm:mt-4 glass-card p-6 rounded-xl border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-[#ccff00]" />
              <h2 className="text-xl font-bold text-white">Profili Figli</h2>
            </div>
            <p className="text-zinc-400 text-sm mb-4">
              Hai più di un figlio iscritto? Inserisci il codice fornito dall'allenatore per collegare il profilo al tuo account.
            </p>
            {!showLinkProfile ? (
              <Button
                onClick={() => setShowLinkProfile(true)}
                variant="outline"
                className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Aggiungi profilo figlio
              </Button>
            ) : (
              <form onSubmit={handleLinkProfile} className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Codice atleta</Label>
                  <Input
                    type="text"
                    value={linkCode}
                    onChange={(e) => setLinkCode(e.target.value.toUpperCase())}
                    placeholder="Es. AB3X7K"
                    maxLength={6}
                    className="bg-zinc-950 border-zinc-800 focus:border-[#ccff00] focus:ring-1 focus:ring-[#ccff00] rounded-xl h-12 font-mono tracking-widest text-[#ccff00] uppercase"
                    required
                  />
                  <p className="text-zinc-500 text-xs">Il codice è visibile nella lista atleti del tuo allenatore</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setShowLinkProfile(false); setLinkCode(''); }}
                    className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl"
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    disabled={linkLoading || linkCode.length < 6}
                    className="flex-1 bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl"
                  >
                    {linkLoading ? 'Collegamento...' : 'Collega'}
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* GESTIONE ACCOUNT */}
          <div className="mt-3 sm:mt-4 glass-card p-6 rounded-xl border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <LogOut className="w-6 h-6 text-[#ccff00]" />
              <h2 className="text-xl font-bold text-white">Gestione Account</h2>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              {hasDualRole && (
                <Button
                  onClick={async () => {
                    try {
                      await switchToCoach();
                    } catch {
                      toast.error('Errore nel cambio profilo');
                    }
                  }}
                  variant="outline"
                  className="flex-1 bg-zinc-800 text-[#ccff00] hover:bg-zinc-700 border border-[#ccff00]/30 rounded-xl h-12"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Profilo Allenatore
                </Button>
              )}
              <Button
                onClick={logout}
                variant="outline"
                className="flex-1 bg-zinc-800 text-white hover:bg-zinc-700 border border-zinc-700 rounded-xl h-12"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>

            <div className="mt-6 pt-4 border-t border-zinc-800">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="text-xs text-zinc-500 hover:text-red-400 transition-colors">
                    Elimina account
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-900 border border-white/10 rounded-xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Sei assolutamente sicuro?</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-400">
                      Questa azione non può essere annullata. Questo eliminerà permanentemente il tuo account e rimuoverà tutti i tuoi dati dai nostri server.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-zinc-800 text-white hover:bg-zinc-700 rounded-xl">
                      Annulla
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAccount}
                      className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
                    >
                      Sì, elimina il mio account
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
